import { lazy } from 'react';

import type { AppRouteObject } from '@/navigation/route-handle';

import AdminLayout from './_layout';

const AdminHomePage = lazy(() => import('./home'));
const FieldMappingsPage = lazy(() => import('./field-mappings-page'));
const AIUsagePage = lazy(() => import('./ai-usage-page'));
const MeshOutboxPage = lazy(() => import('./mesh-outbox-page'));
const MeshConnectivityPage = lazy(() => import('./mesh-connectivity-page'));
const TenantsPage = lazy(() => import('./tenants-page'));
const ProvisionTenantPage = lazy(() => import('./provision-tenant-page'));
const TenantAuthPage = lazy(() => import('./tenant-auth-page'));
const StaffPage = lazy(() => import('./staff-page'));
const FeatureFlagsPage = lazy(() => import('./feature-flags-page'));

/**
 * The admin area is mounted at a static `/admin`, deliberately outside the
 * `/:slug` tree: `scoped/_layout.tsx` sends an invalid or NIL tenant to `/403`,
 * which would fight a cross-tenant route that has no tenant at all.
 *
 * React Router ranks a static segment above a dynamic one, so `/admin` wins
 * over `/:slug` regardless of declaration order. It is declared before the
 * scoped routes anyway, so the precedence is visible rather than inferred.
 *
 * T07-T12 add their pages as children here and an entry in `admin-sections.ts`.
 */
export const adminRoutes: AppRouteObject[] = [
  {
    path: '/admin',
    element: <AdminLayout />,
    handle: { breadcrumb: 'Administration', fullWidth: true },
    children: [
      { index: true, element: <AdminHomePage /> },
      {
        path: 'field-mappings',
        element: <FieldMappingsPage />,
        handle: { breadcrumb: 'Field mappings' },
      },
      {
        path: 'ai-usage',
        element: <AIUsagePage />,
        handle: { breadcrumb: 'AI usage' },
      },
      {
        path: 'mesh-outbox',
        element: <MeshOutboxPage />,
        handle: { breadcrumb: 'Mesh outbox' },
      },
      {
        path: 'mesh-connectivity',
        element: <MeshConnectivityPage />,
        handle: { breadcrumb: 'Mesh connectivity' },
      },
      {
        path: 'tenants',
        element: <TenantsPage />,
        handle: { breadcrumb: 'Tenants and users' },
      },
      {
        path: 'tenants/new',
        element: <ProvisionTenantPage />,
        handle: { breadcrumb: 'New tenant' },
      },
      {
        path: 'tenants/:tenantId/auth',
        element: <TenantAuthPage />,
        handle: { breadcrumb: 'Authentication settings' },
      },
      {
        path: 'staff',
        element: <StaffPage />,
        handle: { breadcrumb: 'Staff' },
      },
      {
        path: 'feature-flags',
        element: <FeatureFlagsPage />,
        handle: { breadcrumb: 'Feature flags' },
      },
    ],
  },
];
